import React, { Component } from 'react'
import AmCharts from "@amcharts/amcharts3-react";

/**
 * REQUIRED SETUP (confirmed missing via a real error - "d.AmMap is not a constructor"):
 * this project's currently-loaded amcharts.js bundle only has the serial/pie chart engine, not
 * the "ammap" (map) engine at all - AmMap doesn't exist on the AmCharts object yet. This
 * component only references the map by name ("indiaLow"); it doesn't ship the map engine or
 * India map data itself, so both need to be added to the project, in this order, AFTER wherever
 * amcharts.js/serial.js/pie.js are currently loaded (the map engine extends that same object):
 *
 *   1. ammap.js     - adds the AmMap map-chart engine itself
 *   2. indiaLow.js  - the actual India state/UT boundary data ("indiaLow" map)
 *
 * If amcharts.js/serial.js/pie.js are loaded via <script> tags (e.g. in public/index.html),
 * add two more tags right after them, in this exact order:
 *   <script src="https://cdn.amcharts.com/lib/3/ammap.js"></script>
 *   <script src="https://cdn.amcharts.com/lib/3/maps/js/indiaLow.js"></script>
 * (Prefer vendoring both files locally alongside your existing amcharts.js/serial.js/pie.js if
 * this app can't reach external CDNs at runtime - same idea, just local paths instead of the
 * amcharts.com URLs above.)
 *
 * If instead they're pulled in via npm/webpack imports somewhere (e.g. a chart-vendor.js that
 * does `import 'amcharts3/amcharts/amcharts'; import 'amcharts3/amcharts/serial'; ...`), add:
 *   import 'amcharts3/amcharts/ammap';
 *   import 'amcharts3/amcharts/maps/js/indiaLow';
 * right after the existing amcharts/serial/pie imports there.
 *
 * Until one of these is done, every question that hits this component will crash the same way
 * (Uncaught TypeError: d.AmMap is not a constructor) - it's not a bug in this file or the query.
 */

// Standard amCharts 3 "indiaLow" map area ids (ISO state/UT codes, as shipped in ammap's
// maps/js/indiaLow.js). merchant_pkyc_crisp.address_state is free text, so this normalizes
// (trim + uppercase) and covers common alternate spellings (Odisha/Orissa, Delhi/NCT of Delhi,
// Puducherry/Pondicherry). Add more entries here if you spot a state name that isn't matching -
// unmatched names are skipped from the map (logged to console), not thrown.
const STATE_NAME_TO_MAP_ID = {
    'ANDHRA PRADESH': 'IN-AP',
    'ARUNACHAL PRADESH': 'IN-AR',
    'ASSAM': 'IN-AS',
    'BIHAR': 'IN-BR',
    'CHHATTISGARH': 'IN-CT',
    'GOA': 'IN-GA',
    'GUJARAT': 'IN-GJ',
    'HARYANA': 'IN-HR',
    'HIMACHAL PRADESH': 'IN-HP',
    'JHARKHAND': 'IN-JH',
    'KARNATAKA': 'IN-KA',
    'KERALA': 'IN-KL',
    'MADHYA PRADESH': 'IN-MP',
    'MAHARASHTRA': 'IN-MH',
    'MANIPUR': 'IN-MN',
    'MEGHALAYA': 'IN-ML',
    'MIZORAM': 'IN-MZ',
    'NAGALAND': 'IN-NL',
    'ODISHA': 'IN-OR',
    'ORISSA': 'IN-OR',
    'PUNJAB': 'IN-PB',
    'RAJASTHAN': 'IN-RJ',
    'SIKKIM': 'IN-SK',
    'TAMIL NADU': 'IN-TN',
    'TELANGANA': 'IN-TG',
    'TRIPURA': 'IN-TR',
    'UTTAR PRADESH': 'IN-UP',
    'UTTARAKHAND': 'IN-UT',
    'WEST BENGAL': 'IN-WB',
    'ANDAMAN AND NICOBAR ISLANDS': 'IN-AN',
    'CHANDIGARH': 'IN-CH',
    'DADRA AND NAGAR HAVELI': 'IN-DN',
    'DAMAN AND DIU': 'IN-DD',
    'DELHI': 'IN-DL',
    'NCT OF DELHI': 'IN-DL',
    'JAMMU AND KASHMIR': 'IN-JK',
    'LADAKH': 'IN-LA',
    'LAKSHADWEEP': 'IN-LD',
    'PUDUCHERRY': 'IN-PY',
    'PONDICHERRY': 'IN-PY'
};

// Fallback for messy free-text state names seen for real (e.g. "Tamilnadu" with no space,
// "Jammukashmir", "CHANDIGHAR" typo) - strips everything but letters and compares against a
// similarly-stripped version of the table above, plus a few explicit typo/alias entries that
// stripping alone can't fix (a missing word, not just a missing space).
function stripNonAlpha(s) {
    return String(s || '').toUpperCase().replace(/[^A-Z]/g, '');
}
const STATE_NORMALIZED_TO_MAP_ID = {};
Object.keys(STATE_NAME_TO_MAP_ID).forEach(name => {
    STATE_NORMALIZED_TO_MAP_ID[stripNonAlpha(name)] = STATE_NAME_TO_MAP_ID[name];
});
Object.assign(STATE_NORMALIZED_TO_MAP_ID, {
    'JAMMUKASHMIR': 'IN-JK',
    'JK': 'IN-JK',
    'CHANDIGHAR': 'IN-CH',   // common typo for Chandigarh
    'NEWDELHI': 'IN-DL'
});

function resolveStateMapId(rawName) {
    const upperTrim = String(rawName || '').trim().toUpperCase();
    if (STATE_NAME_TO_MAP_ID[upperTrim]) return STATE_NAME_TO_MAP_ID[upperTrim];
    return STATE_NORMALIZED_TO_MAP_ID[stripNonAlpha(rawName)] || null;
}

// Lat/long lookup for major Indian cities (bubble markers on the map) - merchant_pkyc_crisp.city
// is free text, so this normalizes (trim + uppercase) and covers common metro/tier-2 names.
// A city not in this list is skipped from the map (console-logged), not from the table/data.
const CITY_NAME_TO_LATLNG = {
    'MUMBAI': [19.0760, 72.8777], 'DELHI': [28.7041, 77.1025], 'NEW DELHI': [28.6139, 77.2090],
    'BANGALORE': [12.9716, 77.5946], 'BENGALURU': [12.9716, 77.5946], 'HYDERABAD': [17.3850, 78.4867],
    'AHMEDABAD': [23.0225, 72.5714], 'CHENNAI': [13.0827, 80.2707], 'KOLKATA': [22.5726, 88.3639],
    'SURAT': [21.1702, 72.8311], 'PUNE': [18.5204, 73.8567], 'JAIPUR': [26.9124, 75.7873],
    'LUCKNOW': [26.8467, 80.9462], 'KANPUR': [26.4499, 80.3319], 'NAGPUR': [21.1458, 79.0882],
    'INDORE': [22.7196, 75.8577], 'THANE': [19.2183, 72.9781], 'BHOPAL': [23.2599, 77.4126],
    'VISAKHAPATNAM': [17.6868, 83.2185], 'PATNA': [25.5941, 85.1376], 'VADODARA': [22.3072, 73.1812],
    'GHAZIABAD': [28.6692, 77.4538], 'LUDHIANA': [30.9010, 75.8573], 'AGRA': [27.1767, 78.0081],
    'NASHIK': [19.9975, 73.7898], 'FARIDABAD': [28.4089, 77.3178], 'MEERUT': [28.9845, 77.7064],
    'RAJKOT': [22.3039, 70.8022], 'VARANASI': [25.3176, 82.9739], 'SRINAGAR': [34.0837, 74.7973],
    'AURANGABAD': [19.8762, 75.3433], 'DHANBAD': [23.7957, 86.4304], 'AMRITSAR': [31.6340, 74.8723],
    'NAVI MUMBAI': [19.0330, 73.0297], 'PRAYAGRAJ': [25.4358, 81.8463], 'ALLAHABAD': [25.4358, 81.8463],
    'RANCHI': [23.3441, 85.3096], 'HOWRAH': [22.5958, 88.2636], 'COIMBATORE': [11.0168, 76.9558],
    'JABALPUR': [23.1815, 79.9864], 'GWALIOR': [26.2183, 78.1828], 'VIJAYAWADA': [16.5062, 80.6480],
    'JODHPUR': [26.2389, 73.0243], 'MADURAI': [9.9252, 78.1198], 'RAIPUR': [21.2514, 81.6296],
    'KOTA': [25.2138, 75.8648], 'GUWAHATI': [26.1445, 91.7362], 'CHANDIGARH': [30.7333, 76.7794],
    'SOLAPUR': [17.6599, 75.9064], 'MYSORE': [12.2958, 76.6394], 'MYSURU': [12.2958, 76.6394],
    'TIRUCHIRAPPALLI': [10.7905, 78.7047], 'BAREILLY': [28.3670, 79.4304], 'ALIGARH': [27.8974, 78.0880],
    'TIRUPPUR': [11.1085, 77.3411], 'GURUGRAM': [28.4595, 77.0266], 'GURGAON': [28.4595, 77.0266],
    'MORADABAD': [28.8386, 78.7733], 'JALANDHAR': [31.3260, 75.5762], 'BHUBANESWAR': [20.2961, 85.8245],
    'SALEM': [11.6643, 78.1460], 'WARANGAL': [17.9784, 79.5941], 'GUNTUR': [16.3067, 80.4365],
    'BHIWANDI': [19.3002, 73.0630], 'SAHARANPUR': [29.9680, 77.5460], 'GORAKHPUR': [26.7606, 83.3732],
    'BIKANER': [28.0229, 73.3119], 'AMRAVATI': [20.9374, 77.7796], 'NOIDA': [28.5355, 77.3910],
    'JAMSHEDPUR': [22.8046, 86.2029], 'BHILAI': [21.1938, 81.3509], 'CUTTACK': [20.4625, 85.8828],
    'FIROZABAD': [27.1592, 78.3957], 'KOCHI': [9.9312, 76.2673], 'COCHIN': [9.9312, 76.2673],
    'NELLORE': [14.4426, 79.9865], 'BHAVNAGAR': [21.7645, 72.1519], 'DEHRADUN': [30.3165, 78.0322],
    'DURGAPUR': [23.5204, 87.3119], 'ASANSOL': [23.6739, 86.9524], 'ROURKELA': [22.2604, 84.8536],
    'NANDED': [19.1383, 77.3210], 'KOLHAPUR': [16.7050, 74.2433], 'AJMER': [26.4499, 74.6399],
    'AKOLA': [20.7002, 77.0082], 'JAMNAGAR': [22.4707, 70.0577], 'UJJAIN': [23.1765, 75.7885],
    'SILIGURI': [26.7271, 88.3953], 'JHANSI': [25.4484, 78.5685], 'JAMMU': [32.7266, 74.8570],
    'MANGALORE': [12.9141, 74.8560], 'MANGALURU': [12.9141, 74.8560], 'ERODE': [11.3410, 77.7172],
    'TIRUNELVELI': [8.7139, 77.7567], 'GAYA': [24.7955, 85.0002], 'JALGAON': [21.0077, 75.5626],
    'UDAIPUR': [24.5854, 73.7125],
    // Districts frequently used as the "city" value instead of a specific town/city.
    'OSMANABAD': [18.1860, 76.0419], 'LATUR': [18.4088, 76.5604], 'SONIPAT': [28.9931, 77.0151],
    'MATHURA': [27.4924, 77.6737], 'MEWAT': [28.1200, 76.9300], 'MOGA': [30.8158, 75.1710],
    'AZAMGARH': [26.0737, 83.1836], 'DEORIA': [26.5024, 83.7791], 'CHANDAULI': [25.2599, 83.2651],
    'MORENA': [26.4959, 78.0009], 'MAHABUB NAGAR': [16.7488, 77.9855], 'MAHBUBNAGAR': [16.7488, 77.9855],
    'KANCHIPURAM': [12.8342, 79.7036], 'TIRUVALLUR': [13.1439, 79.9080],
    'RANGAREDDY': [17.3850, 78.4867], 'BARDHAMAN': [23.2324, 87.8615], 'BURDWAN': [23.2324, 87.8615],
    'BELAGAVI': [15.8497, 74.4977], 'BELGAUM': [15.8497, 74.4977], 'REWARI': [28.1990, 76.6173],
    // Delhi's own internal districts (address_state/city data often names the district, not "Delhi")
    // - approximated to central Delhi so they still show up rather than being dropped from the map.
    'SOUTH DELHI': [28.7041, 77.1025], 'NORTH DELHI': [28.7041, 77.1025], 'WEST DELHI': [28.7041, 77.1025],
    'EAST DELHI': [28.7041, 77.1025], 'NORTH WEST DELHI': [28.7041, 77.1025], 'SOUTH WEST DELHI': [28.7041, 77.1025],
    'NORTH EAST DELHI': [28.7041, 77.1025], 'CENTRAL DELHI': [28.7041, 77.1025], 'NEW DELHI DISTRICT': [28.7041, 77.1025]
};

// amCharts3's ammap doesn't auto-gradient area colors by value - each area's color has to be
// computed and set explicitly. Simple light-blue -> dark-blue interpolation for the heat effect.
function heatColor(value, min, max) {
    if (max === min) return '#3c5bdc';
    const ratio = (value - min) / (max - min);
    const start = { r: 0xdc, g: 0xe6, b: 0xfa };
    const end = { r: 0x1a, g: 0x3d, b: 0x8f };
    const r = Math.round(start.r + (end.r - start.r) * ratio);
    const g = Math.round(start.g + (end.g - start.g) * ratio);
    const b = Math.round(start.b + (end.b - start.b) * ratio);
    return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
}

/**
 * India state-wise heat map.
 * Props:
 *   data        - array of { state_name, value } - unmatched state names are skipped from the
 *                 map (not the underlying table, which the caller renders separately).
 *   total       - grand total across all states (server-computed, e.g. sum of every state's
 *                 value) - rendered as its own "Total: X" line, ABOVE the min/max legend. The
 *                 legend's own min/max only describe the color-scale range (the smallest and
 *                 largest single state values), not a sum, and was being mistaken for the total
 *                 more than once - this line exists so the real total is always visible
 *                 regardless of how the question was phrased. Harmless no-op when omitted.
 *   total_label - unit label for both the total line and the legend (e.g. "merchants acquired",
 *                 "paid transactions"). Defaults to "total" when omitted.
 *   name        - export file name
 *   chartHeight - CSS height string (e.g. "400px")
 */
class IndiaMapChart extends Component {

    render() {
        const rows = this.props.data || [];
        const values = rows.map(r => Number(r.value) || 0);
        const min = values.length ? Math.min(...values) : 0;
        const max = values.length ? Math.max(...values) : 0;

        const areas = [];
        rows.forEach(r => {
            const mapId = resolveStateMapId(r.state_name);
            if (!mapId) {
                console.log('IndiaMapChart: no map id for state "' + r.state_name + '" - skipped from map');
                return;
            }
            const value = Number(r.value) || 0;
            areas.push({
                id: mapId,
                title: r.state_name,
                value: value,
                color: heatColor(value, min, max),
                balloonText: r.state_name + ': <b>' + value + '</b>'
            });
        });

        // City-level bubble markers, sized by value (min 8px, max 28px diameter) - overlaid on
        // top of the state areas above. Cities not in CITY_NAME_TO_LATLNG are skipped (logged),
        // same "don't drop the underlying row, just don't plot it" rule as unmatched states.
        const cityRows = this.props.city_data || [];
        const cityValues = cityRows.map(r => Number(r.value) || 0);
        const cityMin = cityValues.length ? Math.min(...cityValues) : 0;
        const cityMax = cityValues.length ? Math.max(...cityValues) : 0;
        const MIN_BUBBLE = 8, MAX_BUBBLE = 28;

        const images = [];
        cityRows.forEach(r => {
            const coords = CITY_NAME_TO_LATLNG[String(r.city_name || '').trim().toUpperCase()];
            if (!coords) {
                console.log('IndiaMapChart: no lat/long for city "' + r.city_name + '" - skipped from map');
                return;
            }
            const value = Number(r.value) || 0;
            const size = cityMax === cityMin
                ? (MIN_BUBBLE + MAX_BUBBLE) / 2
                : MIN_BUBBLE + ((value - cityMin) / (cityMax - cityMin)) * (MAX_BUBBLE - MIN_BUBBLE);
            images.push({
                "type": "circle",
                "latitude": coords[0],
                "longitude": coords[1],
                "width": size,
                "height": size,
                "color": "#dc3912",
                "selectable": true,
                "title": r.city_name,
                "balloonText": r.city_name + (r.state_name ? ' (' + r.state_name + ')' : '') + ': <b>' + value + '</b>'
            });
        });

        let fileName = this.props.name;
        if (fileName === null || fileName === 'null' || fileName === undefined || fileName === 'undefined') {
            fileName = "chartData";
        }

        const config = {
            "type": "map",
            "theme": "light",
            "dataProvider": {
                "map": "indiaLow",
                "areas": areas,
                "images": images
            },
            "areasSettings": {
                "autoZoom": true,
                "unlistedAreasColor": "#EAEAEA",
                "outlineColor": "#FFFFFF",
                "selectedColor": "#1a3d8f",
                "rollOverOutlineColor": "#333333",
                "balloonText": "[[balloonText]]"
            },
            "imagesSettings": {
                "alpha": 0.75,
                "outlineAlpha": 0.9,
                "outlineColor": "#FFFFFF",
                "rollOverOutlineColor": "#000000",
                "rollOverScale": 1.3,
                "selectedScale": 1.3,
                "balloonText": "[[balloonText]]"
            },
            // Image export (PNG) needs amCharts' export plugin to pull in extra libraries
            // (canvg/pdfmake) that aren't loaded in this project - that lazy-load was failing
            // (vfs_fonts.js coming back as an HTML 404 page instead of JS) and crashing the page.
            // Disabled here; the underlying data is already in the chat's table if export is needed.
            "export": {
                "enabled": false,
                "fileName": fileName.replace(/ /g, "_"),
                "menu": [{ "class": "export-main", "menu": ["PNG"] }]
            }
        };

        const totalLabel = this.props.total_label || 'total';
        const hasTotal = typeof this.props.total === 'number';

        return (
            <div className="chart_div">
                <AmCharts.React style={{ width: "100%", height: this.props.chartHeight || "400px" }} options={config} />
                {hasTotal &&
                    <div style={{
                        textAlign: 'center', fontSize: '14px', fontWeight: 600, color: '#1a3d8f', marginTop: '6px'
                    }}>
                        Total: {this.props.total.toLocaleString('en-IN')} {totalLabel}
                    </div>
                }
                {values.length > 0 &&
                    <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '8px', fontSize: '11px', color: '#666', marginTop: '4px'
                    }}>
                        <span>{min}</span>
                        <div style={{
                            width: '120px', height: '10px', borderRadius: '4px',
                            background: 'linear-gradient(to right, ' + heatColor(min, min, max) + ', ' + heatColor(max, min, max) + ')'
                        }}></div>
                        <span>{max}</span>
                        <span style={{ marginLeft: '6px' }}>{totalLabel} (state max)</span>
                    </div>
                }
            </div>
        );
    }
}

export default IndiaMapChart;